import { ActionIcon, Card, Group, Loader, Select, Text, Title, Tooltip } from "@mantine/core";
import { IconRefresh } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { useEffect, useState } from "react";
import BreadcrumbsNavigation from "@/components/breadcrumbs-navigation";
import NoData from "@/components/no-data";
import { getAllStartedRegulamentosIdeias } from "@/modules/regulamento-ideias/firestore";
import useUserStore from "@/modules/users/store";
import useGetRegulamentosIdeias from "@/src/routes/gestao-inovacao-page/useGetRegulamentos";
import RankingDeColaboradoresList from "./RankingDeColaboradoresList";

type TGetAllStartedRegulamentosIdeiasResponse = Awaited<ReturnType<typeof getAllStartedRegulamentosIdeias>>;

/**
 * Retorna somente os IDs dos regulamentos em ranking (incluindo finalizados)
 * @returns - docs getAllStartedRegulamentosIdeias
 */
function useGetRankingRegulamentoPerColaborador() {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  return useQuery<TGetAllStartedRegulamentosIdeiasResponse, FirestoreError, string[]>({
    queryKey: [activeEmpresaId, "getRankingRegulamentoPerColaboradorDocs"],
    queryFn: () => {
      if (!activeEmpresaId) {
        throw new Error("Nenhuma empresa ativa no momento");
      }
      return getAllStartedRegulamentosIdeias(activeEmpresaId);
    },
    select(snapshot) {
      if (snapshot.length === 0) {
        return [];
      }
      return snapshot.map((document_) => document_.id);
    }
  });
}

const RankingDeColaboradoresPage = () => {
  // lista completa de regulamentos (com id + nome)
  const { data: regulamentosIdeias = [] } = useGetRegulamentosIdeias();

  // lista de IDs que têm movimentação
  const { isLoading, data: regulamentosIds = [], refetch } = useGetRankingRegulamentoPerColaborador();

  const [selectedRegulamento, setSelectedRegulamento] = useState<string | null>(null);

  // define valor inicial
  useEffect(() => {
    if (regulamentosIds.length > 0) {
      setSelectedRegulamento(regulamentosIds[0] || "");
    } else {
      setSelectedRegulamento(null);
    }
  }, [regulamentosIds]);

  // monta o array { value, label } filtrando apenas os IDs relevantes
  const selectData = regulamentosIdeias
    .filter((reg) => regulamentosIds.includes(reg.id))
    .map((reg) => ({
      value: reg.id,
      label: reg.nome // ajuste para o campo de nome correto
    }));

  const hasNoMovimentacao = !isLoading && selectData.length === 0;

  const selectedRegulamentoObject = regulamentosIdeias.find((reg) => reg.id === selectedRegulamento);

  /**
   *
   */
  function handleRefreshClick() {
    void refetch({ cancelRefetch: true });
  }

  return (
    <>
      <BreadcrumbsNavigation notHome>
        <Text c="dimmed">Atividades</Text>
      </BreadcrumbsNavigation>
      <Card withBorder>
        <Card.Section inheritPadding py="md">
          <Group justify="space-between">
            <Title order={3} fw="normal">
              Ranking De Colaboradores
            </Title>
            <Group>
              <Select
                label="Selecione o regulamento"
                placeholder="Regulamento do ranking"
                disabled={isLoading || hasNoMovimentacao}
                data={selectData}
                value={selectedRegulamento}
                onChange={setSelectedRegulamento}
              />
              <Tooltip label="Atualizar">
                <ActionIcon variant="subtle" onClick={handleRefreshClick} loading={isLoading} mt="lg">
                  <IconRefresh />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>
        </Card.Section>

        <Card.Section inheritPadding py="md">
          {isLoading && <Loader />}
          {!isLoading && selectedRegulamentoObject && (
            <RankingDeColaboradoresList regulamento={selectedRegulamentoObject} />
          )}
          {hasNoMovimentacao && <NoData>Nenhuma movimentação encontrada</NoData>}
        </Card.Section>
      </Card>
    </>
  );
};

export default RankingDeColaboradoresPage;
